import { Button, ButtonProps } from '@mui/material';
import { styled } from '@mui/material';

import { COLORS } from '~/styles/colors';

type SizeOption = 'xs' | 'small' | 'middle';

interface StyledButtonProps extends ButtonProps {
  $size?: SizeOption;
  $textColor?: string;
  $backgroundColor?: string;
  $hoverBackgroundColor?: string;
  $borderColor?: string;
  $isPrimaryOutlined?: boolean;
}

const sizes: Record<SizeOption, string> = {
  xs: '4px 16px',
  small: '5px 20px',
  middle: '16px 44px',
};

const fontSizes: Record<SizeOption, string> = {
  xs: '15px',
  small: '18px',
  middle: '18px',
};

const lineHeights: Record<SizeOption, string> = {
  xs: '20px',
  small: '22px',
  middle: '22px',
};

export const StyledButton = styled(Button, {
  shouldForwardProp: (prop) =>
    ![
      '$size',
      '$textColor',
      '$backgroundColor',
      '$hoverBackgroundColor',
      '$borderColor',
      '$isPrimaryOutlined',
    ].includes(prop as string),
})<StyledButtonProps>(
  ({ $size = 'middle', $textColor, $backgroundColor, $hoverBackgroundColor, $borderColor, $isPrimaryOutlined }) => ({
    '&.MuiButton-contained': {
      background: $backgroundColor || `linear-gradient(to right, ${COLORS.blue600}, ${COLORS.primary})`,
      fontSize: fontSizes[$size],
      lineHeight: lineHeights[$size],
      padding: sizes[$size],
      borderRadius: '100px',
      boxShadow: 'none',
      color: $textColor,
      textTransform: 'none',

      '.MuiTouchRipple-root': {
        display: 'none',
      },

      '&:hover': {
        background: $hoverBackgroundColor || `linear-gradient(to right, ${COLORS.blue350}, ${COLORS.blue200})`,
        boxShadow: 'none',
        color: $textColor,
      },

      '&:disabled': {
        background: COLORS.grey200,
        cursor: 'not-allowed',
      },

      '&.MuiButtonBase-root': {
        padding: sizes[$size],
      },
    },

    '&.MuiButton-outlined': {
      fontSize: fontSizes[$size],
      lineHeight: lineHeights[$size],
      padding: sizes[$size],
      borderRadius: '100px',
      border: `1.5px solid ${$borderColor}`,
      boxShadow: 'none',
      background: 'transparent',
      color: $textColor,
      textTransform: 'none',
      minWidth: 'unset',

      '.MuiTouchRipple-root': {
        display: 'none',
      },

      '&:disabled': {
        borderColor: $isPrimaryOutlined ? COLORS.grey200 : COLORS.grey400,
        color: $isPrimaryOutlined ? COLORS.black038 : COLORS.grey400,
        cursor: 'not-allowed',
        backgroundColor: $isPrimaryOutlined ? COLORS.grey200 : undefined,
      },

      '&:hover': {
        borderColor: $borderColor,
        color: $textColor,
        boxShadow: 'none',
      },

      '&.MuiButtonBase-root': {
        padding: sizes[$size],
      },
    },
  }),
);
